/**
 * Contenido de ayuda contextual por sección.
 * Mapeo de patrones de ruta a contenido explicativo.
 *
 * El matching es por prefijo — la primera entrada que coincida gana.
 * Por eso las rutas más específicas van primero.
 */

export interface HelpAction {
  emoji: string;
  titulo: string;
  desc: string;
}

export interface HelpSeccion {
  titulo: string;
  intro: string;
  acciones: HelpAction[];
  tips?: string[];
}

export const HELP_DEFAULT: HelpSeccion = {
  titulo: "RedesIP CRM",
  intro:
    "Sistema integral de gestión para tu negocio: reservas, clientes, delivery, menú digital y mucho más. " +
    "Usá la barra superior para navegar entre secciones, o el botón de ayuda en cualquier momento.",
  acciones: [
    { emoji: "🏠", titulo: "Dashboard", desc: "Vista resumen con accesos rápidos." },
    { emoji: "📋", titulo: "Reservas", desc: "Gestión completa del flujo de reservas." },
    { emoji: "🛒", titulo: "Delivery", desc: "Catálogo, pedidos y repartidores." },
    { emoji: "👥", titulo: "Clientes", desc: "Análisis 360° y campañas." },
    { emoji: "⚙️", titulo: "Configuración", desc: "Sucursales, branding, integraciones." },
  ],
};

/** Mapeo ordenado: las rutas más específicas primero. */
export const HELP_BY_RUTA: Array<{ match: RegExp; content: HelpSeccion }> = [
  // ──────────── RESERVAS ────────────
  {
    match: /^\/tienda\/lista-espera/,
    content: {
      titulo: "Lista de espera",
      intro:
        "Cuando una zona del local llega a su capacidad máxima por hora, las nuevas reservas entran en lista de espera. " +
        "Cuando alguien cancela, el sistema promueve automáticamente al primero en orden de llegada (FIFO).",
      acciones: [
        { emoji: "👁️", titulo: "Ver", desc: "Abre el detalle completo de la reserva en espera." },
        { emoji: "⬆️", titulo: "Promover", desc: "Pasa manualmente esta reserva a 'pendiente' sin esperar una cancelación. Notifica al cliente por email y WhatsApp." },
        { emoji: "📞", titulo: "Llamar / Email", desc: "Contactá directamente al cliente desde los datos visibles." },
      ],
      tips: [
        "Las reservas se promueven solas cuando alguien cancela su reserva en la misma zona+fecha+hora.",
        "Un cron diario a las 3 AM marca como 'auto_cancelada' las reservas en espera cuya fecha ya pasó.",
        "Si una reserva fue promovida pero el cliente no confirma en 4 horas, se libera y se promueve al siguiente.",
      ],
    },
  },
  {
    match: /^\/reservas/,
    content: {
      titulo: "Reservas",
      intro:
        "Gestioná todas las reservas confirmadas, pendientes y canceladas. Confirmá llegadas, asigná mesas, " +
        "registrá no-shows y devolvé clientes a la operación.",
      acciones: [
        { emoji: "✅", titulo: "Confirmar", desc: "Marca la reserva como confirmada y envía email + WhatsApp con QR de check-in." },
        { emoji: "❌", titulo: "Cancelar", desc: "Cancela la reserva (registra motivo opcional). Si hay lista de espera, promueve la siguiente automáticamente." },
        { emoji: "🪑", titulo: "Asignar mesa", desc: "Asocia la reserva a una mesa específica (cuando llega el cliente)." },
        { emoji: "👋", titulo: "Marcar atendida", desc: "Cierra la reserva cuando el cliente terminó su consumo." },
        { emoji: "🚫", titulo: "No-show", desc: "El cliente no se presentó. Libera el cupo y aplica política si está configurada." },
      ],
      tips: [
        "El badge en el menú muestra cuántas reservas tenés pendientes de confirmar.",
        "Las reservas en lista de espera no aparecen aquí — andá a /tienda/lista-espera.",
      ],
    },
  },
  {
    match: /^\/calendario/,
    content: {
      titulo: "Calendario de reservas",
      intro: "Vista diaria y mensual de la ocupación de tu local. Detectá días saturados, huecos y patrones.",
      acciones: [
        { emoji: "📅", titulo: "Cambiar vista", desc: "Alterná entre día, semana o mes." },
        { emoji: "👆", titulo: "Click en día", desc: "Ve el detalle de reservas de ese día con sus horarios." },
        { emoji: "🔍", titulo: "Filtros", desc: "Filtrá por sucursal y estado de reservas." },
      ],
    },
  },
  {
    match: /^\/tienda\/ocupacion/,
    content: {
      titulo: "Ocupación en tiempo real",
      intro: "Vista visual de qué mesas/pistas están ocupadas, libres o reservadas en cada momento.",
      acciones: [
        { emoji: "🟢", titulo: "Mesa libre", desc: "Disponible para walk-in o reserva." },
        { emoji: "🟡", titulo: "Reservada", desc: "Tiene una reserva próxima — no asignar a walk-in." },
        { emoji: "🔴", titulo: "Ocupada", desc: "Cliente en uso actualmente." },
      ],
      tips: ["La página se refresca sola cada minuto. Ideal para pantalla del salón."],
    },
  },
  {
    match: /^\/tienda\/walkin/,
    content: {
      titulo: "Lista de espera (walk-in)",
      intro:
        "Clientes sin reserva que se anotan escaneando el QR de la puerta. Gestioná la cola en vivo: " +
        "quién espera, a quién ya llamaste y quién fue atendido.",
      acciones: [
        { emoji: "📣", titulo: "Llamar", desc: "Marca al cliente como 'llamado' — se actualiza cada 12s en pantalla." },
        { emoji: "✅", titulo: "Atendido", desc: "Cierra la entrada cuando el cliente ya fue sentado." },
        { emoji: "↩️", titulo: "Devolver a espera", desc: "Si llamaste por error o el cliente todavía no llegó." },
        { emoji: "🚫", titulo: "No se presentó", desc: "Libera el turno si el cliente no respondió al llamado." },
      ],
      tips: ["Es distinto de 'Reservas' — acá entran los clientes que llegan sin reserva previa."],
    },
  },
  {
    match: /^\/tienda\/pagos-reservas/,
    content: {
      titulo: "Pagos de reserva",
      intro:
        "Verificá los comprobantes de pago anticipado que suben los clientes para garantizar su reserva " +
        "en zonas que lo requieren.",
      acciones: [
        { emoji: "✅", titulo: "Aprobar", desc: "Verifica el comprobante y confirma el pago — la reserva queda garantizada." },
        { emoji: "❌", titulo: "Rechazar", desc: "Si el comprobante no es válido; podés dejar un motivo visible para el cliente." },
        { emoji: "🖼️", titulo: "Ver comprobante", desc: "Abre la imagen/captura que subió el cliente." },
        { emoji: "🔍", titulo: "Buscar", desc: "Por nombre, cédula o número de referencia." },
      ],
    },
  },
  {
    match: /^\/tienda\/bloqueos/,
    content: {
      titulo: "Bloqueos de disponibilidad",
      intro:
        "Cerrá fechas, horarios, zonas o mesas puntuales para mantenimiento, torneos o eventos — sin tener que " +
        "tocar la configuración general de horarios de cada zona.",
      acciones: [
        { emoji: "➕", titulo: "Nuevo bloqueo", desc: "Elegí tipo (mantenimiento, evento, torneo, etc.), rango de fechas/horas y alcance." },
        { emoji: "🎯", titulo: "Alcance", desc: "Todo el local, zonas específicas, o mesas puntuales." },
        { emoji: "🗑️", titulo: "Eliminar", desc: "Libera la disponibilidad bloqueada antes de tiempo." },
      ],
    },
  },

  // ──────────── DELIVERY ────────────
  {
    match: /^\/delivery/,
    content: {
      titulo: "Tablero Delivery",
      intro:
        "Centro de control de pedidos delivery: nuevos, en preparación, listos, en camino y entregados. " +
        "Asigná repartidores, marcá estados y notificá al cliente con un click.",
      acciones: [
        { emoji: "📋", titulo: "Confirmar pedido", desc: "Pasa el pedido a 'en preparación' y notifica a cocina." },
        { emoji: "🛵", titulo: "Asignar repartidor", desc: "Selecciona un repartidor en turno para llevar el pedido." },
        { emoji: "✅", titulo: "Marcar listo", desc: "El pedido salió de cocina, listo para retirar/entregar." },
        { emoji: "📞", titulo: "Contactar cliente", desc: "Click en el teléfono o email para llamar/escribir." },
      ],
      tips: [
        "Los repartidores con su rol especial sólo ven sus pedidos asignados, no este tablero.",
        "Cuando el repartidor marca 'salí con el pedido', el cliente recibe WhatsApp automático.",
      ],
    },
  },
  {
    match: /^\/repartidor/,
    content: {
      titulo: "Panel del repartidor",
      intro: "Vista mobile-first para repartidores. Sólo ves los pedidos asignados a vos.",
      acciones: [
        { emoji: "🟢", titulo: "Iniciar turno", desc: "Te marca como disponible. El admin podrá asignarte pedidos." },
        { emoji: "🛵", titulo: "Salí con el pedido", desc: "Marca 'en camino' y notifica al cliente por WhatsApp automáticamente." },
        { emoji: "🧭", titulo: "Cómo llegar", desc: "Abre Google Maps con la dirección del cliente." },
        { emoji: "📞", titulo: "Llamar / WhatsApp", desc: "Contactá al cliente con un toque." },
        { emoji: "✅", titulo: "Marcar entregado", desc: "Cierra el pedido y registra tu comisión." },
      ],
      tips: [
        "En el tab 'Historial' ves todas tus entregas con el total de comisiones acumuladas — útil para liquidaciones.",
      ],
    },
  },
  {
    match: /^\/articulos-config/,
    content: {
      titulo: "Configuración de artículos",
      intro:
        "Decidí qué artículos del POS aparecen en tu menú digital y cuáles están disponibles para delivery. " +
        "Subí fotos, agregá descripciones largas, marcá agotados y aplicá un markup masivo a los precios delivery.",
      acciones: [
        { emoji: "💰", titulo: "Markup precios delivery", desc: "Aplicá un % de sobreprecio masivo a todos los precios de delivery (ej: +15% sobre POS)." },
        { emoji: "✏️", titulo: "Editar artículo", desc: "Cambiá categoría, foto, descripción, precio override y tiempo de preparación." },
        { emoji: "🔄", titulo: "Toggle menú/delivery", desc: "Activa o desactiva el artículo de cada canal por separado." },
        { emoji: "🔥", titulo: "Destacado / Agotado", desc: "Marcá productos para que se vean priorizados o tapados como sin stock." },
      ],
    },
  },
  {
    match: /^\/tienda\/categorias/,
    content: {
      titulo: "Categorías de artículos",
      intro:
        "Agrupá tus productos para el menú digital y delivery. Una categoría sirve para ambos canales — cada artículo " +
        "decide si aparece en menú, delivery o ambos.",
      acciones: [
        { emoji: "➕", titulo: "Nueva categoría", desc: "Creá categorías como 'Pizzas', 'Bebidas', 'Postres'." },
        { emoji: "📥", titulo: "Importar del POS", desc: "Lee las familias de tu sistema ICG y crea categorías + asigna artículos automáticamente." },
        { emoji: "⬆️⬇️", titulo: "Reordenar", desc: "Las flechas cambian el orden en que aparecen en el menú." },
        { emoji: "🚫", titulo: "Toggle Menú / Delivery", desc: "Definí si la categoría aparece solo en menú, solo en delivery, o ambos." },
      ],
    },
  },
  {
    match: /^\/pasarelas-pago/,
    content: {
      titulo: "Pasarelas de pago",
      intro:
        "Configurá los métodos de pago que aceptás en el checkout de delivery: PayPal, Stripe, Pago Móvil, " +
        "Zelle, transferencias bancarias y más.",
      acciones: [
        { emoji: "➕", titulo: "Activar pasarela", desc: "Activá los métodos que aceptás y completá los datos." },
        { emoji: "🔑", titulo: "Credenciales", desc: "Para PayPal y Stripe, ingresá tu API key. Para otros, los datos de cuenta." },
        { emoji: "💬", titulo: "Mensaje al cliente", desc: "Personalizá qué instrucciones ve el cliente al elegir cada método." },
      ],
    },
  },

  // ──────────── EVENTOS Y ENCUESTAS ────────────
  {
    match: /^\/tienda\/eventos-publicos/,
    content: {
      titulo: "Eventos públicos",
      intro:
        "Vendé entradas a eventos con cupo limitado y pago anticipado — los clientes los ven y compran desde el " +
        "catálogo público (/eventos).",
      acciones: [
        { emoji: "➕", titulo: "Crear evento", desc: "Título, fecha, precio, cupo máximo e imagen (ideal 1200x630px, formato de preview de redes)." },
        { emoji: "📤", titulo: "Publicar", desc: "Pasa de 'Borrador' a 'Publicado' — recién ahí es visible en el catálogo público." },
        { emoji: "💳", titulo: "Pestaña Pagos", desc: "Verificá o rechazá los comprobantes de pago de entradas, con motivo opcional." },
        { emoji: "🔒", titulo: "Cerrar / Cancelar", desc: "Cerrá la venta cuando se agote el cupo, o cancelá el evento (notifica a compradores)." },
      ],
      tips: ["No confundir con 'Eventos' (/tienda/eventos) — eso es para eventos privados (cumpleaños, corporativos), no venta pública de entradas."],
    },
  },
  {
    match: /^\/tienda\/eventos$/,
    content: {
      titulo: "Eventos privados",
      intro:
        "Gestioná reservas de salón para eventos privados (cumpleaños, corporativos): lista de invitados, " +
        "checklist de tareas del evento y check-in el día del evento.",
      acciones: [
        { emoji: "➕", titulo: "Nuevo evento", desc: "Fecha, zona, organizador y notas internas." },
        { emoji: "📋", titulo: "Checklist", desc: "Tareas con estado (pendiente → en proceso → espera → culminado) y responsable." },
        { emoji: "📥", titulo: "Importar invitados", desc: "Pegá una lista o subí un Excel — nombre, email, teléfono, acompañantes." },
        { emoji: "✅", titulo: "Check-in", desc: "Marcá invitados como 'llegó' o 'no show' el día del evento." },
        { emoji: "📊", titulo: "Exportar", desc: "Descargá la lista de invitados y su estado en Excel." },
      ],
    },
  },
  {
    match: /^\/tienda\/encuestas/,
    content: {
      titulo: "Encuestas",
      intro:
        "Creá encuestas de satisfacción, generá su link/QR y enviálas masivamente por WhatsApp a tu base de clientes.",
      acciones: [
        { emoji: "➕", titulo: "Nueva encuesta", desc: "Definí las preguntas y activá el QR." },
        { emoji: "📨", titulo: "Enviar masivo", desc: "Manda la encuesta por WhatsApp a todos los clientes con teléfono registrado." },
        { emoji: "🔗", titulo: "Copiar / Descargar QR", desc: "Para imprimir en mesas o compartir el link directo." },
        { emoji: "📊", titulo: "Ver resultados", desc: "Estadísticas de respuestas de cada encuesta." },
      ],
      tips: ["Para que el personal de salón solo muestre el QR sin poder editar encuestas, usá /encuestas-qr."],
    },
  },
  {
    match: /^\/encuestas-qr/,
    content: {
      titulo: "Encuestas (mostrar QR)",
      intro:
        "Pantalla de solo lectura para el personal con contacto directo con el cliente: mostrarle el QR de una " +
        "encuesta activa en grande para que lo escanee, sin poder crear ni editar encuestas.",
      acciones: [
        { emoji: "👆", titulo: "Tocar la encuesta", desc: "Abre el QR en pantalla completa para que el cliente lo escanee con su celular." },
      ],
    },
  },

  // ──────────── CLIENTES / MARKETING ────────────
  {
    match: /^\/audiencias/,
    content: {
      titulo: "Audiencias",
      intro:
        "Creá segmentos de clientes por criterios (gasto, frecuencia, última visita, etc.) para usarlos en " +
        "campañas de marketing masivo.",
      acciones: [
        { emoji: "➕", titulo: "Nueva audiencia", desc: "Armá los criterios con el constructor visual (ej: 'gastó más de $50 en los últimos 30 días')." },
        { emoji: "📥", titulo: "Exportar", desc: "Bajá la lista de clientes del segmento." },
        { emoji: "🗑️", titulo: "Eliminar", desc: "Borra la audiencia guardada (no afecta a los clientes)." },
      ],
      tips: ["Las audiencias son dinámicas — se recalculan según los criterios, no son una lista fija."],
    },
  },
  {
    match: /^\/customers\/\d+/,
    content: {
      titulo: "Detalle de cliente",
      intro: "Ficha individual del cliente con su segmento RFM, valor de vida (LTV) y última compra.",
      acciones: [
        { emoji: "📊", titulo: "Segmento RFM", desc: "Clasificación automática por recencia, frecuencia y monto gastado." },
      ],
      tips: ["Para el análisis completo de hábitos de consumo, usá Cliente 360°."],
    },
  },
  {
    match: /^\/customers/,
    content: {
      titulo: "Clientes",
      intro:
        "Listado de clientes con su segmento RFM (recencia/frecuencia/monto) y valor de vida estimado. " +
        "Ordená y buscá para identificar rápido a tus mejores clientes.",
      acciones: [
        { emoji: "🔍", titulo: "Buscar", desc: "Por nombre." },
        { emoji: "↕️", titulo: "Ordenar columnas", desc: "Click en el encabezado — por LTV, última compra, segmento, etc." },
      ],
      tips: ["Para el análisis profundo de hábitos (qué pide siempre, con quién viene), usá Cliente 360°."],
    },
  },

  // ──────────── CLIENTE 360 ────────────
  {
    match: /^\/cliente360\/\d+/,
    content: {
      titulo: "Detalle del cliente",
      intro: "Toda la información de un cliente: historial, hábitos, productos favoritos, hábitos por contexto (solo o grupo).",
      acciones: [
        { emoji: "📞", titulo: "Contactar", desc: "Email, WhatsApp o llamada con un click." },
        { emoji: "✏️", titulo: "Editar datos", desc: "Completá cumpleaños, sexo, CIF y otros datos del cliente." },
        { emoji: "🔮", titulo: "Sugerir sexo", desc: "El sistema sugiere el sexo basado en el primer nombre." },
        { emoji: "📊", titulo: "Hábitos de consumo", desc: "Las 3 vistas: Lo que siempre pide / Composición del ticket / Combos predecibles." },
      ],
      tips: [
        "La edad estimada por cédula aparece cuando no hay fecha de nacimiento real.",
        "Hábitos por contexto: el sistema distingue entre cuando vino solo (1 comensal) o con grupo (2+).",
      ],
    },
  },
  {
    match: /^\/cliente360/,
    content: {
      titulo: "Cliente 360°",
      intro:
        "Análisis profundo de tu base de clientes con datos reales del POS. Buscá clientes, ve KPIs avanzados, " +
        "identificá VIPs y exportá segmentos para campañas masivas.",
      acciones: [
        { emoji: "🔍", titulo: "Buscar", desc: "Por nombre, email o teléfono." },
        { emoji: "👑", titulo: "Ver KPIs avanzados", desc: "Comparativa por sexo, distribución por edad, top familias, hábitos del local, segmento VIP y en riesgo." },
        { emoji: "📥", titulo: "Exportar CSV", desc: "Bajá el segmento VIP o en riesgo como CSV para análisis o campaña externa." },
        { emoji: "📨", titulo: "Campaña masiva", desc: "Envía email o WhatsApp a un segmento completo con plantillas personalizables." },
      ],
    },
  },

  // ──────────── CONFIG ────────────
  {
    match: /^\/sucursales/,
    content: {
      titulo: "Sucursales y zonas",
      intro:
        "Configurá tus locales y dentro de cada uno las zonas (terraza, salón principal, barra). Definí horarios, " +
        "capacidad reservable por hora y reglas por zona.",
      acciones: [
        { emoji: "🪑", titulo: "Capacidad por hora", desc: "Si tu zona tiene 10 mesas pero solo querés que se reserven 5 por hora — ponelo acá. El resto entra a lista de espera." },
        { emoji: "⏰", titulo: "Horario semanal", desc: "Por zona: en qué días y horas se aceptan reservas." },
        { emoji: "📋", titulo: "Política de cancelación", desc: "Cuánto antes puede cancelar el cliente sin costo." },
        { emoji: "🚫", titulo: "Máx por cliente", desc: "Cuántas reservas máximas puede tener un cliente en X días." },
      ],
    },
  },
  {
    match: /^\/tienda\/menu-config/,
    content: {
      titulo: "Configuración del menú digital",
      intro: "Personalizá el menú público que ven tus clientes al escanear el QR.",
      acciones: [
        { emoji: "🖼️", titulo: "Logo y colores", desc: "Define el aspecto visual." },
        { emoji: "📄", titulo: "Subir PDFs", desc: "Cargá tus cartas en PDF. Pueden ser varias (Comida, Bebidas, Postres)." },
        { emoji: "🛎️", titulo: "Llamar mesonero / Pedir cuenta", desc: "Botones flotantes que el cliente puede tocar desde la mesa." },
      ],
    },
  },
  {
    match: /^\/tienda\/email-templates/,
    content: {
      titulo: "Plantillas de email y WhatsApp",
      intro:
        "Centralizá los textos de todos los correos automáticos y mensajes de campaña. " +
        "Editá una vez, se aplica a todo el sistema.",
      acciones: [
        { emoji: "📧", titulo: "Plantillas de Reservas", desc: "Reserva recibida, confirmada, cancelada, recordatorios, lista de espera." },
        { emoji: "📨", titulo: "Plantillas de Campañas", desc: "VIP, re-engagement, cumpleaños — usadas por las campañas masivas desde Cliente 360." },
        { emoji: "🔤", titulo: "Variables", desc: "Usá {{primer_nombre}}, {{fechaReserva}}, etc. — el sistema las reemplaza por cliente." },
        { emoji: "🏪", titulo: "Override por sucursal", desc: "Una plantilla por defecto + variantes específicas por local." },
      ],
    },
  },
  {
    match: /^\/usuarios/,
    content: {
      titulo: "Usuarios del sistema",
      intro: "Crear cuentas para tu equipo con permisos según su rol.",
      acciones: [
        { emoji: "👤", titulo: "Recepción", desc: "Ve y gestiona reservas." },
        { emoji: "🍳", titulo: "Cocina", desc: "Ve calendario y eventos." },
        { emoji: "🛵", titulo: "Repartidor", desc: "Solo ve sus pedidos asignados, marca entregas y registra honorarios." },
        { emoji: "👑", titulo: "Supervisor de tienda", desc: "Acceso completo a la sucursal." },
        { emoji: "⚙️", titulo: "Administrador", desc: "Acceso a todo el sistema, todas las sucursales." },
      ],
    },
  },
  {
    match: /^\/tienda\/qr/,
    content: {
      titulo: "Códigos QR",
      intro: "Generá e imprimí los QR de menú/reservas por sucursal y zona, para colocar en las mesas.",
      acciones: [
        { emoji: "⬇️", titulo: "Descargar PNG", desc: "Baja el código QR en alta resolución, listo para imprimir." },
        { emoji: "🔗", titulo: "Abrir link", desc: "Previsualiza a dónde apunta el QR antes de imprimirlo." },
      ],
    },
  },
  {
    match: /^\/tienda\/whatsapp/,
    content: {
      titulo: "WhatsApp",
      intro:
        "Conectá el número de WhatsApp del negocio para enviar confirmaciones de reserva y delivery automáticas, " +
        "y configurá el bot de respuestas (FAQ, reservas, menú).",
      acciones: [
        { emoji: "🔌", titulo: "Elegir proveedor", desc: "Evolution API (gratis, escaneás QR), Meta Cloud (oficial), o Twilio." },
        { emoji: "🤖", titulo: "Funciones del bot", desc: "Activá qué puede responder solo: reservas, delivery, menú, preguntas frecuentes." },
        { emoji: "❓", titulo: "FAQ", desc: "Cargá preguntas y respuestas con palabras clave para que el bot las detecte." },
        { emoji: "💬", titulo: "Mensaje de bienvenida", desc: "El primer mensaje que recibe un cliente nuevo." },
      ],
    },
  },
  {
    match: /^\/tienda\/delivery-zonas/,
    content: {
      titulo: "Zonas de delivery",
      intro: "Definí las zonas de cobertura de delivery y el costo de envío de cada una.",
      acciones: [
        { emoji: "➕", titulo: "Nueva zona", desc: "Nombre y costo de envío." },
        { emoji: "🔀", titulo: "Activar/desactivar", desc: "Sin borrar la zona, dejá de ofrecerla temporalmente." },
        { emoji: "↕️", titulo: "Reordenar", desc: "Cambiá el orden en que aparecen al cliente." },
      ],
    },
  },
  {
    match: /^\/tienda\/modulos/,
    content: {
      titulo: "Módulos de tu negocio",
      intro:
        "Activá o desactivá las funciones que usa tu organización (delivery, eventos, encuestas, audiencias, etc.). " +
        "Lo desactivado desaparece del menú para todo tu equipo.",
      acciones: [
        { emoji: "🔀", titulo: "Toggle", desc: "Prende o apaga cada módulo individualmente." },
        { emoji: "💾", titulo: "Guardar", desc: "Los cambios se aplican cuando cada usuario vuelve a iniciar sesión." },
      ],
    },
  },
  {
    match: /^\/admin\/modulos/,
    content: {
      titulo: "Módulos globales (todas las organizaciones)",
      intro:
        "Mismo control de módulos que en 'Módulos de tu negocio', pero para TODAS las organizaciones del sistema " +
        "— solo visible para super admin.",
      acciones: [
        { emoji: "🏢", titulo: "Por organización", desc: "Cada tenant tiene su propio set de módulos activos." },
        { emoji: "🔀", titulo: "Toggle y guardar", desc: "Los cambios son por organización — no afectan a las demás." },
      ],
      tips: ["Herramienta sensible: desactivar un módulo en uso rompe esa función para todo el personal de esa organización."],
    },
  },
  {
    match: /^\/smtp-config/,
    content: {
      titulo: "Configuración de correo (SMTP)",
      intro:
        "Conectá tu propia cuenta de email para que las confirmaciones de reserva y campañas se envíen desde tu dominio, " +
        "en vez del remitente genérico del sistema.",
      acciones: [
        { emoji: "✉️", titulo: "Datos del servidor", desc: "Host, puerto, usuario y contraseña de tu proveedor de email." },
        { emoji: "🧪", titulo: "Enviar prueba", desc: "Mandate un email de prueba antes de confiar en la configuración." },
        { emoji: "🔌", titulo: "Activar/desactivar", desc: "Sin borrar los datos guardados, podés pausar el envío por tu SMTP propio." },
      ],
    },
  },
  {
    match: /^\/sincronizacion/,
    content: {
      titulo: "Sincronización con el POS",
      intro:
        "Estado del agente que sincroniza datos del POS (ICG) hacia el CRM: ventas, clientes, mesas, artículos. " +
        "Útil para diagnosticar por qué Cliente 360° o reportes no muestran datos recientes.",
      acciones: [
        { emoji: "🟢🟡🔴", titulo: "Estado por sucursal", desc: "Verde = sincronizó hace menos de 1h, ámbar = 1-3h, rojo = más de 3h o nunca." },
        { emoji: "📜", titulo: "Log detallado", desc: "Cada corrida del agente, con errores específicos si falló." },
        { emoji: "🔄", titulo: "Actualizar", desc: "Refresca el estado sin esperar el refresco automático de 1 minuto." },
      ],
      tips: ["Si una sucursal aparece en gris ('Sin datos'), revisá que el agente en la máquina del POS tenga configurado el SUCURSAL_ID correcto."],
    },
  },
  {
    match: /^\/admin\/diagnosticos/,
    content: {
      titulo: "Diagnósticos del sistema",
      intro:
        "Herramienta técnica de solo super admin: consultas predefinidas para detectar problemas comunes " +
        "(reservas duplicadas, mesas sin capacidad, tareas vencidas, etc.) y estado general del servidor.",
      acciones: [
        { emoji: "▶️", titulo: "Consultas predefinidas", desc: "Un click ejecuta chequeos comunes de integridad de datos." },
        { emoji: "💻", titulo: "SQL libre", desc: "Corré consultas propias contra la base — úsalo con cuidado." },
      ],
      tips: ["Esta pantalla ejecuta SQL directo contra producción — solo para diagnóstico de lectura, no para 'arreglar' datos a mano sin estar seguro."],
    },
  },
  {
    match: /^\/reportes/,
    content: {
      titulo: "Reportes",
      intro:
        "Analítica de reservas por período (día/semana/mes o rango personalizado): estados, origen del cliente " +
        "(web, WhatsApp, walk-in, etc.) y tendencia de clientes nuevos vs. recurrentes.",
      acciones: [
        { emoji: "📅", titulo: "Cambiar período", desc: "Día, semana, mes o rango de fechas personalizado." },
        { emoji: "🏢", titulo: "Filtrar por sucursal", desc: "Si tenés varias, comparalas o mirá una sola." },
        { emoji: "📊", titulo: "Desglose por estado/origen", desc: "Cuántas reservas se cancelaron, confirmaron, vinieron por WhatsApp, etc." },
      ],
    },
  },
  {
    match: /^\/perfil/,
    content: {
      titulo: "Tu perfil",
      intro: "Tus datos personales, contraseña y actividad reciente dentro del sistema.",
      acciones: [
        { emoji: "✏️", titulo: "Editar datos", desc: "Nombre y teléfono." },
        { emoji: "🔑", titulo: "Cambiar contraseña", desc: "Pedí tu contraseña actual para confirmarlo." },
        { emoji: "📋", titulo: "Actividad / Checklist", desc: "Historial de acciones tuyas sobre reservas y tareas de checklist pendientes asignadas a vos." },
      ],
    },
  },

  // ──────────── DASHBOARD ────────────
  {
    match: /^\/$/,
    content: {
      titulo: "Dashboard principal",
      intro:
        "Vista ejecutiva para administradores: accesos agrupados en Operación diaria, Catálogo & Cobros, " +
        "y Administración del sistema.",
      acciones: [
        { emoji: "📂", titulo: "Secciones por color", desc: "Cada bloque agrupa funciones relacionadas con su propio color identificador." },
        { emoji: "🔢", titulo: "Badges", desc: "Números junto a cada módulo muestran pendientes (reservas, tareas, etc.)." },
      ],
    },
  },
  {
    match: /^\/tienda$/,
    content: {
      titulo: "Dashboard de tu tienda",
      intro:
        "Vista central de tu local. Las secciones están agrupadas por contexto: Operación del día, Delivery & Catálogo, " +
        "Inteligencia & Comunicación y Configuración.",
      acciones: [
        { emoji: "⚡", titulo: "Quick Stats", desc: "Indicadores rápidos arriba: reservas pendientes, tareas, alertas." },
        { emoji: "🎨", titulo: "Secciones de colores", desc: "Cada bloque tiene su color identificador y los módulos relacionados." },
        { emoji: "🔢", titulo: "Badges", desc: "El número rojo junto a 'Reservas' muestra las pendientes de confirmar." },
      ],
    },
  },
];

/** Devuelve el contenido de ayuda para una ruta dada. */
export function getHelpForRoute(pathname: string): HelpSeccion {
  for (const entry of HELP_BY_RUTA) {
    if (entry.match.test(pathname)) return entry.content;
  }
  return HELP_DEFAULT;
}

// ────────────────────────────────────────────────────────────
// GUÍA DE CONFIGURACIÓN INICIAL — pasos esenciales
// ────────────────────────────────────────────────────────────
export interface OnboardingPaso {
  id: string;
  titulo: string;
  descripcion: string;
  emoji: string;
  ruta: string;
  esencial: boolean;
  /** ¿Cómo detectar si ya está hecho? — el componente lo evalúa */
  detectar?: (estado: Record<string, any>) => boolean;
}

export const ONBOARDING_PASOS: OnboardingPaso[] = [
  {
    id: "logo",
    titulo: "Cargá el logo de tu local",
    descripcion: "Aparece en menú digital, emails de confirmación y como favicon en el navegador.",
    emoji: "🎨",
    ruta: "/tienda/menu-config",
    esencial: true,
    detectar: (e) => !!e.tieneLogo,
  },
  {
    id: "sucursal_config",
    titulo: "Configurá horarios y capacidad de zonas",
    descripcion: "Definí en cada zona (terraza, salón, etc.) los horarios de atención y máximo de mesas reservables por hora.",
    emoji: "🪑",
    ruta: "/sucursales",
    esencial: true,
    detectar: (e) => !!e.sucursalZonasConfiguradas,
  },
  {
    id: "categorias",
    titulo: "Importá categorías del POS",
    descripcion: "Con un click traés todas las familias de tu ICG. Después podés renombrar o reordenar.",
    emoji: "📂",
    ruta: "/tienda/categorias",
    esencial: true,
    detectar: (e) => (e.numCategorias ?? 0) > 0,
  },
  {
    id: "articulos",
    titulo: "Configurá tus artículos para delivery",
    descripcion: "Subí fotos, marcá cuáles aparecen en menú digital y cuáles en delivery. Aplicá markup masivo si querés.",
    emoji: "🛒",
    ruta: "/articulos-config",
    esencial: true,
    detectar: (e) => (e.numArticulosDelivery ?? 0) > 0,
  },
  {
    id: "pasarelas",
    titulo: "Activá métodos de pago",
    descripcion: "Configurá las pasarelas que aceptás: PayPal, Stripe, Pago Móvil, Zelle, etc.",
    emoji: "💳",
    ruta: "/pasarelas-pago",
    esencial: true,
    detectar: (e) => (e.numPasarelasActivas ?? 0) > 0,
  },
  {
    id: "menu_pdf",
    titulo: "Subí tu menú en PDF",
    descripcion: "Los clientes que escaneen el QR podrán ver tu carta. Si tenés varias (comida, bebidas, postres), subí todas.",
    emoji: "📄",
    ruta: "/tienda/menu-config",
    esencial: false,
    detectar: (e) => (e.numPdfs ?? 0) > 0,
  },
  {
    id: "smtp",
    titulo: "Configurá tu cuenta de email (SMTP)",
    descripcion: "Para que los emails de confirmación de reservas y campañas se envíen desde tu dominio.",
    emoji: "📧",
    ruta: "/smtp-config",
    esencial: true,
    detectar: (e) => !!e.smtpConfigurado,
  },
  {
    id: "whatsapp",
    titulo: "Conectá WhatsApp Business",
    descripcion: "Para enviar confirmaciones de reservas y notificaciones de delivery automáticas por WhatsApp.",
    emoji: "💬",
    ruta: "/tienda/whatsapp",
    esencial: false,
    detectar: (e) => !!e.waConfigurado,
  },
  {
    id: "plantillas",
    titulo: "Personalizá las plantillas de email",
    descripcion: "Adaptá el tono y el contenido de los emails automáticos al estilo de tu marca.",
    emoji: "📨",
    ruta: "/tienda/email-templates",
    esencial: false,
  },
  {
    id: "usuarios",
    titulo: "Creá usuarios para tu equipo",
    descripcion: "Recepción, cocina, repartidores, supervisores — cada uno con sus permisos.",
    emoji: "👥",
    ruta: "/usuarios",
    esencial: false,
    detectar: (e) => (e.numUsuarios ?? 1) > 1,
  },
];
